import { datadogLogs } from '@datadog/browser-logs'
import { useQueryState } from 'nuqs'
import { useCallback } from 'react'
import { useCartEntryToReplace } from '@/components/ProductPage/useCartEntryToReplace'
import type { ProductOfferFragment } from '@/services/graphql/generated'
import { getSortedOffers } from '@/utils/offers.utils'
import { usePriceIntent } from '../usePriceIntent'

export const SELECTED_OFFER_QUERY_PARAM = 'selectedOffer'

type ComparableProductOffer = Pick<ProductOfferFragment, 'deductible'> & {
  variant: Pick<ProductOfferFragment['variant'], 'typeOfContract'>
}

const compareOffer = (a: ComparableProductOffer, b: ComparableProductOffer) => {
  if (a.variant.typeOfContract !== b.variant.typeOfContract) return false
  if (JSON.stringify(a.deductible) !== JSON.stringify(b.deductible)) return false
  return true
}

const useSelectedOfferValue = () => {
  const { priceIntent } = usePriceIntent()
  const entryToReplace = useCartEntryToReplace()
  const [selectedOfferId] = useQueryState(SELECTED_OFFER_QUERY_PARAM)

  if (!priceIntent || priceIntent.offers.length === 0) {
    return null
  }

  if (selectedOfferId) {
    const matchingOffer = priceIntent.offers.find((item) => item.id === selectedOfferId)

    if (matchingOffer) {
      return matchingOffer
    }
  }

  if (entryToReplace) {
    const matchingOffer = priceIntent.offers.find((item) => compareOffer(item, entryToReplace))

    if (matchingOffer) {
      return matchingOffer
    }

    // Do we want to return null here? What should happen if we don't have any matching offers?
    return null
  }

  const priceMatchedOffer = priceIntent.offers.find((item) => item.priceMatch != null)
  if (priceMatchedOffer) {
    return priceMatchedOffer
  }

  const defaultOffer = priceIntent.offers.find((offer) => offer.id === priceIntent.defaultOffer?.id)
  if (defaultOffer) {
    return defaultOffer
  }

  return getSortedOffers(priceIntent.offers)[0]
}

export const useSelectedOffer = () => {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [_, setSelectedOfferId] = useQueryState(SELECTED_OFFER_QUERY_PARAM)

  const selectedOffer = useSelectedOfferValue()

  const setSelectedOffer = useCallback(
    (offer: ProductOfferFragment | null): void => {
      setSelectedOfferId(offer?.id ?? null)

      if (offer?.priceMatch) {
        datadogLogs.logger.info('Selected price matched offer', {
          offerId: offer.id,
          product: offer.product.name,
          productVariant: offer.variant.displayName,
          externalInsurer: offer.priceMatch.externalInsurer.displayName,
          priceReduction: offer.priceMatch.priceReduction.amount,
        })
      }
    },
    [setSelectedOfferId],
  )

  return [selectedOffer, setSelectedOffer] as const
}

export const useSelectedOfferValueOrThrow = (): ProductOfferFragment => {
  const selectedOffer = useSelectedOfferValue()

  if (selectedOffer == null) {
    throw new Error('selectedOffer must have value')
  }
  return selectedOffer
}
